import {deleteCookie, getCookie, getCookies, setCookie} from 'cookies-next';
import {InitialContextService, ContextService} from 'core/services/ContextService';
import {TmpCookiesObj} from 'cookies-next/lib/types';

interface ICookieService {
    setInsecureCookie(key: string, val: string | null): void;
    get(key: string): string | null;
    getAll(): TmpCookiesObj;
    deleteInsecureCookie(key: string): void;
}

/**
 * Cookie которые имеют флаг `httpOnly` не видны на клиенте вовсе,
 * их нельзя менять. Т.е., если на клиенте мы задаем куки `example-cookie-name`,
 * которые уже есть в браузере, но с флагом `httpOnly` не произойдет ничего.
 *
 * Поэтому для перезаписи таких куков необходимо использовать метод `setSecureCookie`,
 * который дернет специальный метод апи, и перезапишет защищенные куки.
 */
export class CookieService implements ICookieService {
    private static instance: CookieService;
    public static REWRITE_COOKIE_PREFIX = 'rewrite:';

    private constructor() {}

    private getContext() {
        // контекст в зависимости от того, где выполняется код getInitialProps/getServerSideProps
        return {
            req: ContextService.req || InitialContextService.req,
            res: ContextService.res || InitialContextService.res,
        };
    }

    public setInsecureCookie(key: string, val: string | null, expires?: Date, domain?: string) {
        const context = this.getContext();
        setCookie(key, val, {
            domain,
            expires,
            httpOnly: false,
            req: context.req,
            res: context.res,
        });
    }

    public setSecureCookie(key: string, val: string | null, expires?: Date, domain?: string) {
        const context = this.getContext();
        setCookie(key, val, {
            domain,
            expires,
            httpOnly: true,
            req: context.req,
            res: context.res,
        });
    }

    public get(key: string): string {
        const context = this.getContext();
        return getCookie(key, {req: context.req, res: context.res})?.toString() || '';
    }

    public getAll() {
        const context = this.getContext();
        return getCookies({req: context.req, res: context.res});
    }

    public deleteInsecureCookie(key: string) {
        const context = this.getContext();
        deleteCookie(key, {httpOnly: false, req: context.req, res: context.res});
    }

    public deleteSecureCookie(key: string) {
        const context = this.getContext();
        deleteCookie(key, {httpOnly: true, req: context.req, res: context.res});
    }

    public static getInstance(): CookieService {
        if (!CookieService.instance) {
            CookieService.instance = new CookieService();
        }

        return CookieService.instance;
    }
}
